import { Component } from '@theme/component'
import { onCartAdd, onProductSelectUpdate } from '@efs/events'
import { DialogComponent, DialogCloseEvent } from '@theme/dialog'
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities'

/**
 * EFS quick add — replaces Horizon's quick-add.js (importmap `@theme/quick-add` points here, see snippets/scripts.liquid).
 * Instead of fetching and morphing the whole product page, the modal loads the lean section
 * `efs-quick-add-content` via the Section Rendering API (cached per URL), keeps image/links in sync
 * with the selected variant and returns focus to the opening button.
 * Element names stay `quick-add-component` / `quick-add-dialog` (markup from Horizon's snippets).
 */
const QUICK_ADD_SECTION_ID = 'efs-quick-add-content'

/** How long the dialog stays open after a successful add, so the button's checkmark animation is visible (ms). */
const ADDED_FEEDBACK_DURATION = 1200

export class QuickAddComponent extends Component {
    /** @type {AbortController | null} */
    #abortController = null
    /** @type {Map<string, string>} */
    #cachedContent = new Map()
    /** @type {HTMLElement | null} */
    #lastTrigger = null

    get productPageUrl() {
        const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'))
        const productLink = productCard?.getProductCardLink()

        if (!productLink?.href) return ''

        const url = new URL(productLink.href)

        if (url.searchParams.has('variant')) {
            return url.toString()
        }

        const selectedVariantId = this.#getSelectedVariantId()
        if (selectedVariantId) {
            url.searchParams.set('variant', selectedVariantId)
        }

        return url.toString()
    }

    /**
     * Gets the currently selected variant ID from the product card
     * @returns {string | null} The variant ID or null
     */
    #getSelectedVariantId() {
        const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'))
        return productCard?.getSelectedVariantId() || null
    }

    connectedCallback() {
        super.connectedCallback()

        mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal)
    }

    disconnectedCallback() {
        super.disconnectedCallback()

        mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal)
        this.#abortController?.abort()
    }

    /**
     * Handles quick add button click
     * @param {Event} event - The click event
     */
    handleClick = async (event) => {
        event.preventDefault()

        // Opener-Button merken, damit wir den Fokus nach dem Schließen des Dialogs
        // gezielt dorthin zurückgeben können. Das verhindert, dass das native
        // Focus-Return des <dialog> auf einem großen Container landet und dort
        // einen Focus-Outline-Rahmen zeichnet.
        const trigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : null
        this.#lastTrigger = trigger ?? (event.target instanceof HTMLElement ? event.target.closest('button') : null)

        const currentUrl = this.productPageUrl
        let sectionHtml = this.#cachedContent.get(currentUrl)

        if (!sectionHtml) {
            sectionHtml = await this.fetchProductPage(currentUrl)
            if (sectionHtml) {
                this.#cachedContent.set(currentUrl, sectionHtml)
            }
        }

        if (sectionHtml) {
            this.#updateQuickAddModal(sectionHtml)
        }

        this.#openQuickAddModal()
    }

    /** @param {QuickAddDialog} dialogComponent */
    #stayVisibleUntilDialogCloses(dialogComponent) {
        this.toggleAttribute('stay-visible', true)

        dialogComponent.addEventListener(
            DialogCloseEvent.eventName,
            () => {
                this.toggleAttribute('stay-visible', false)

                // Fokus explizit auf den Auslöser-Button zurückgeben. Das native
                // Focus-Return des <dialog> landet in manchen Konstellationen auf
                // einem großen Container und zeichnet dort einen focus-visible
                // Outline-Rahmen um die umgebende Section.
                const trigger = this.#lastTrigger
                this.#lastTrigger = null
                if (trigger && trigger.isConnected) {
                    trigger.focus({ preventScroll: true })
                }
            },
            { once: true }
        )
    }

    #openQuickAddModal = () => {
        const dialogComponent = document.getElementById('quick-add-dialog')
        if (!(dialogComponent instanceof QuickAddDialog)) return

        this.#stayVisibleUntilDialogCloses(dialogComponent)

        dialogComponent.showDialog()
    }

    #closeQuickAddModal = () => {
        const dialogComponent = document.getElementById('quick-add-dialog')
        if (!(dialogComponent instanceof QuickAddDialog)) return

        dialogComponent.closeDialog()
    }

    /**
     * Fetches the quick-add section content via Section Rendering API.
     * Keeps Horizon's method name: product-card.js calls it to prefetch on hover / variant change.
     * @param {string} productPageUrl - The URL of the product page
     * @returns {Promise<string | null>} The section HTML string or null
     */
    async fetchProductPage(productPageUrl) {
        if (!productPageUrl) return null

        this.#abortController?.abort()
        this.#abortController = new AbortController()

        try {
            const url = new URL(productPageUrl)
            url.searchParams.set('sections', QUICK_ADD_SECTION_ID)

            const response = await fetch(url.toString(), {
                signal: this.#abortController.signal,
            })

            if (!response.ok) {
                throw new Error(`Failed to fetch quick-add section: HTTP error ${response.status}`)
            }

            const json = await response.json()
            return json[QUICK_ADD_SECTION_ID] || null
        } catch (error) {
            if (error.name === 'AbortError') {
                return null
            }
            throw error
        } finally {
            this.#abortController = null
        }
    }

    /**
     * Updates the quick-add modal with the fetched section HTML
     * @param {string} sectionHtml - The section HTML string
     */
    #updateQuickAddModal(sectionHtml) {
        const modalContent = document.getElementById('quick-add-modal-content')
        if (!modalContent) return

        modalContent.innerHTML = sectionHtml

        this.#syncVariantSelection(modalContent)
    }

    /**
     * Syncs the variant selection from the product card to the modal
     * @param {Element} modalContent - The modal content element
     */
    #syncVariantSelection(modalContent) {
        const selectedVariantId = this.#getSelectedVariantId()
        if (!selectedVariantId) return

        const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]')
        for (const input of modalInputs) {
            if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
                input.checked = true
                input.dispatchEvent(new Event('change', { bubbles: true }))
                break
            }
        }
    }
}

if (!customElements.get('quick-add-component')) {
    customElements.define('quick-add-component', QuickAddComponent)
}

class QuickAddDialog extends DialogComponent {
    #abortController = new AbortController()

    connectedCallback() {
        super.connectedCallback()

        const { signal } = this.#abortController
        onCartAdd(this, this.handleCartUpdate, { signal })
        onProductSelectUpdate(this, this.#handleVariantUpdate, { signal })
        this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose)
    }

    disconnectedCallback() {
        super.disconnectedCallback()

        this.#abortController.abort()
        clearTimeout(this.#closeTimeout)
        this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose)
    }

    /**
     * Closes the dialog on successful cart add
     * @param {import('@efs/events').CartAdd} add
     */
    handleCartUpdate = ({ didError }) => {
        if (didError) return
        // Keep the dialog open briefly so the "added" checkmark animation on the button is visible
        clearTimeout(this.#closeTimeout)
        this.#closeTimeout = setTimeout(() => this.closeDialog(), ADDED_FEEDBACK_DURATION)
    }

    /** @type {number | undefined} */
    #closeTimeout

    /**
     * Updates image and links when the variant changes
     * @param {import('@efs/events').ProductSelectUpdate} update
     */
    #handleVariantUpdate = ({ resource: variant }) => {
        if (!variant) return

        // Update product image to the variant's featured image
        const img = /** @type {HTMLImageElement | null} */ (this.querySelector('.efs-quick-add__media img'))
        if (img && variant.featured_image) {
            const newSrc = variant.featured_image.src
            if (newSrc && img.src !== newSrc) {
                img.src = newSrc
                img.srcset = ''
                if (variant.featured_image.alt) {
                    img.alt = variant.featured_image.alt
                }
            }
        }

        // Update product links to point to the selected variant
        const variantUrl = variant.url
        if (variantUrl) {
            for (const link of this.querySelectorAll('.efs-quick-add__title-link, .efs-quick-add__view-details-link, .efs-quick-add__media a')) {
                if (link instanceof HTMLAnchorElement) {
                    link.href = variantUrl
                }
            }
        }
    }

    #handleDialogClose = () => {
        const iosVersion = getIOSVersion()
        if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return

        requestAnimationFrame(() => {
            /** @type {HTMLElement | null} */
            const grid = document.querySelector('#ResultsList [product-grid-view]')
            if (grid) {
                const currentWidth = grid.getBoundingClientRect().width
                grid.style.width = `${currentWidth - 1}px`
                requestAnimationFrame(() => {
                    grid.style.width = ''
                })
            }
        })
    }
}

if (!customElements.get('quick-add-dialog')) {
    customElements.define('quick-add-dialog', QuickAddDialog)
}
